import {
    RequestService,
    RequestServiceResponse,
} from '@tcc-online/request-service';
import ProjectTranslation from '../types/project/ProjectTranslation';
import Scale, { type ScaleValue } from '../types/project/360/Scale';
import { type AutoTranslateTranslationsResult } from '../types/AutoTranslateResult';
import SortParams from '../types/SortParams';

const Admin360ScaleService = {
    getTranslations,
    addLanguage,
    removeLanguage,
    saveTranslations,
    getScales,
    getScale,
    createScale,
    updateScale,
    patchValues,
    saveTranslation,
    autoTranslate,
};

const BASE_URL = '/admin/products/360/scales';

const getTranslationsUrl = (
    scaleId: number,
    locale: string,
    translationId?: number,
): string =>
    `${BASE_URL}/${scaleId}/translations${translationId !== null && translationId !== undefined ? `/${translationId}` : ''}?locale=${locale}`;

async function getScales(payload?: SortParams & { search?: string }) {
    return await RequestService.get<Scale[]>(BASE_URL, {
        sortBy: payload?.sortBy,
        sortOrder: payload?.sortOrder,
        search: payload?.search,
    });
}

async function getScale(payload: { scaleId: number }) {
    const { scaleId } = payload;
    return await RequestService.get<Scale>(`${BASE_URL}/${scaleId}`);
}

async function createScale(payload?: {
    name?: string;
    primaryLanguage?: string;
}) {
    return await RequestService.post<
        { name?: string; primaryLanguage?: string },
        Scale
    >(BASE_URL, {
        name: payload?.name,
        primaryLanguage: payload?.primaryLanguage,
    });
}

async function updateScale(payload: {
    scaleId: number;
    name?: string;
    primaryLanguage?: string;
    description?: string;
    values?: ScaleValue[];
}) {
    const { scaleId, name, primaryLanguage, description, values } = payload;
    return await RequestService.patch<
        {
            name?: string;
            primaryLanguage?: string;
            description?: string;
            values?: ScaleValue[];
        },
        Scale
    >(`${BASE_URL}/${scaleId}`, {
        name,
        primaryLanguage,
        values,
        description,
    });
}

async function patchValues(payload: { scaleId: number; values: ScaleValue[] }) {
    const { scaleId, values } = payload;
    return await RequestService.patch<
        {
            values: ScaleValue[];
        },
        Scale
    >(`${BASE_URL}/${scaleId}`, {
        values,
    });
}

async function getTranslations(payload: {
    scaleId: number;
    locale: string;
}): Promise<RequestServiceResponse<ProjectTranslation[]>> {
    const { scaleId, locale } = payload;
    return await RequestService.get<ProjectTranslation[]>(
        getTranslationsUrl(scaleId, locale),
    );
}

async function addLanguage(payload: {
    scaleId: number;
    locale: string;
    locales: string[];
}): Promise<RequestServiceResponse<Scale>> {
    const { scaleId, locale, locales } = payload;
    return await RequestService.patch<{ languages: string[] }, Scale>(
        `${BASE_URL}/${scaleId}`,
        {
            languages: [...locales, locale],
        },
    );
}

async function removeLanguage(payload: {
    scaleId: number;
    locale: string;
    locales: string[];
}): Promise<RequestServiceResponse<Scale>> {
    const { scaleId, locale, locales } = payload;
    return await RequestService.patch<{ languages: string[] }, Scale>(
        `${BASE_URL}/${scaleId}`,
        {
            languages: locales.filter((l) => l !== locale),
        },
    );
}

async function saveTranslations(payload: {
    scaleId: number;
    locale: string;
    translations: ProjectTranslation[];
}): Promise<RequestServiceResponse<unknown>> {
    const { scaleId, locale, translations } = payload;
    return await RequestService.patch<unknown>(
        getTranslationsUrl(scaleId, locale),
        translations,
    );
}
async function saveTranslation(payload: {
    scaleId: number;
    locale: string;
    translation: ProjectTranslation;
}): Promise<RequestServiceResponse<unknown>> {
    const { scaleId, locale, translation } = payload;
    const { id, value } = translation;
    return await RequestService.patch<unknown>(
        getTranslationsUrl(scaleId, locale, id),
        { value },
    );
}

async function autoTranslate(payload: {
    scaleId: number;
    locale: string;
}): Promise<RequestServiceResponse<AutoTranslateTranslationsResult>> {
    const { scaleId, locale } = payload;
    return await RequestService.post<
        undefined,
        AutoTranslateTranslationsResult
    >(`${BASE_URL}/${scaleId}/translations/auto-translate?locale=${locale}`);
}

export default Admin360ScaleService;
